import { normalizeDisplayStatus } from "../../../utils/pricingOptions";

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const clone = (value) => {
  if (value == null) return value;
  if (
    typeof window !== "undefined" &&
    typeof window.structuredClone === "function"
  ) {
    try {
      return window.structuredClone(value);
    } catch {}
  }
  return JSON.parse(JSON.stringify(value));
};

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null);

export const normalizeDecorationLocationKey = (value) =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

export const getDecorationLocation = (decoration = {}) =>
  String(decoration?.Location ?? decoration?.location ?? "").trim();

export const getProductDecorations = (product = {}) => {
  const candidates = [
    product?.Decorations,
    product?.decorations,
    product?.Decoration,
    product?.decoration,
  ].map(asArray);

  return candidates.find((candidate) => candidate.length > 0) || candidates[0] || [];
};

const identityValue = (product = {}) =>
  firstValue(
    product?.cartLineId,
    product?.CartLineId,
    product?.itemId,
    product?.OrderItemId,
    product?.orderItemId,
    product?.persistedOrderItemId,
    product?.id,
  );

export const getCartProductCopyKey = (product = {}, fallbackIndex = 0) => {
  const cartLineId = firstValue(product?.cartLineId, product?.CartLineId);
  if (cartLineId !== undefined && cartLineId !== null && cartLineId !== "") {
    return `cart:${cartLineId}`;
  }
  const identity = identityValue(product);
  return identity !== undefined && identity !== null && identity !== ""
    ? `item:${identity}`
    : `index:${fallbackIndex}`;
};

export const isSameCartProduct = (left = {}, right = {}) => {
  const leftCartLine = firstValue(left?.cartLineId, left?.CartLineId);
  const rightCartLine = firstValue(right?.cartLineId, right?.CartLineId);
  if (leftCartLine != null && rightCartLine != null) {
    return String(leftCartLine) === String(rightCartLine);
  }

  const leftId = identityValue(left);
  const rightId = identityValue(right);
  return leftId != null && rightId != null && String(leftId) === String(rightId);
};

export const findMatchingDecorationLocations = (product = {}, sourceLocation = "") => {
  const sourceKey = normalizeDecorationLocationKey(sourceLocation);
  if (!sourceKey) return [];

  return getProductDecorations(product)
    .map((decoration, index) => ({
      decoration,
      index,
      location: getDecorationLocation(decoration),
    }))
    .filter((entry) => normalizeDecorationLocationKey(entry.location) === sourceKey);
};

const normalizeArtworkForCopy = (artwork = {}) => {
  const copied = {
    ImprintType: artwork?.imprintType ?? artwork?.ImprintType ?? "",
    Charge: artwork?.charge ?? artwork?.Charge ?? 0,
    ProofRequired: artwork?.proofRequired ?? artwork?.ProofRequired ?? "",
    ProofRequiredAction:
      artwork?.proofRequiredAction ?? artwork?.ProofRequiredAction ?? null,
    LogoName: artwork?.logoName ?? artwork?.LogoName ?? "",
    LogoSize: artwork?.logoSize ?? artwork?.LogoSize ?? "",
    LogoColor: artwork?.logoColor ?? artwork?.LogoColor ?? "",
    RepeatLogo: artwork?.repeatLogo ?? artwork?.RepeatLogo ?? "",
    SupplierNotes: artwork?.supplierNotes ?? artwork?.SupplierNotes ?? "",
    CustomerFileId: artwork?.customerFileId ?? artwork?.CustomerFileId ?? null,
    Image:
      artwork?.selectedImage ??
      artwork?.Image ??
      artwork?.originalUrl ??
      artwork?.filePath ??
      null,
    PreviewUrl:
      artwork?.previewUrl ??
      artwork?.PreviewUrl ??
      artwork?.previewImageUrl ??
      artwork?.PreviewImageUrl ??
      null,
  };

  return copied;
};

// Classification travels with each copied row; Matrix lookups are never required.
const chargeKind = (row = {}) => {
  const explicit = String(
    row?.SubType || row?.subType || row?.chargeType || row?.ChargeType ||
    row?.priceType || row?.PriceType || ""
  ).trim().toLowerCase();
  if (explicit === "run" || explicit === "run charge") return "run";
  if (explicit === "fixed" || explicit === "fixed charge" || explicit === "setup charge") return "fixed";
  if (row?.isRunCharge === true) return "run";
  // No inference from a charge's display name. Unclassified rows keep their quantities.
  return "unknown";
};

// Resolve historical Matrix rows by charge ID, never by a display label.
// Custom supplier rows require their own persisted Run/Fixed classification.
export const resolveMatrixChargeTypesForCopy = async (decoration = {}, fetchMatrix) => {
  const copy = clone(decoration) || {};
  const details = copy.DecoratorDetails ?? copy.decoratorDetails ?? {};
  if (String(details.source ?? "").toLowerCase() !== "elite") return copy;
  const rows = Array.isArray(copy.rows) ? copy.rows : asArray(copy.Charge ?? copy.charge);
  const unresolved = rows.filter((row) => chargeKind(row) === "unknown" && (row.chargeId ?? row.ChargeId) != null);
  if (!unresolved.length) return copy;
  const decoratorId = details.decoratorId ?? details.DecoratorId ?? unresolved[0].decoratorId;
  if (decoratorId == null || typeof fetchMatrix !== "function") {
    throw new Error("Unable to verify the Matrix charge type. Re-select the charge before copying.");
  }
  const matrix = await fetchMatrix(decoratorId, null, unresolved[0].chargeId, "all");
  const available = asArray(matrix?.Decorations ?? matrix?.decorations);
  const map = new Map(available.map((entry) => [String(entry.id), entry.ChargeType]));
  const updated = rows.map((row) => {
    if (chargeKind(row) !== "unknown") return row;
    const chargeId = row.chargeId ?? row.ChargeId;
    if (chargeId == null) return row;
    const type = map.get(String(chargeId));
    if (type !== "Run" && type !== "Fixed") {
      throw new Error("Matrix charge type could not be verified. Re-select the charge before copying.");
    }
    return { ...row, SubType: type === "Run" ? "Run Charge" : "Fixed Charge",
      subType: type === "Run" ? "Run Charge" : "Fixed Charge" };
  });
  if (Array.isArray(copy.rows)) copy.rows = updated;
  else copy.Charge = updated;
  return copy;
};

const normalizeChargeForCopy = (row = {}, fallbackDecoratorId = null) => {
  const quantity = Number(row?.quantity ?? row?.Quantity ?? 0);
  const netCost = Number(row?.netCost ?? row?.NetCost ?? 0);
  const margin = Number(row?.margin ?? row?.Margin ?? 0);
  const retail = Number(row?.retail ?? row?.Retail ?? 0);
  const clientPrice = Number(
    row?.clientPrice ?? row?.ClientPrice ?? row?.retail ?? row?.Retail ?? 0
  );
  const total = Number(
    row?.total ??
    row?.Total ??
    (Number.isFinite(quantity) && Number.isFinite(clientPrice)
      ? quantity * clientPrice
      : 0)
  );
  const kind = chargeKind(row);
  const isRunCharge = kind === "run";
  const isBreakdown =
    row?.priceType === "Breakdown" ||
    row?.Type === "BreakDown" ||
    row?.type === "BreakDown";

  return {
    Name: row?.chargeName ?? row?.Name ?? "",
    Quantity: Number.isFinite(quantity) ? quantity : 0,
    NetCost: Number.isFinite(netCost) ? netCost : 0,
    Margin: Number.isFinite(margin) ? margin : 0,
    Retail: Number.isFinite(retail) ? retail : 0,
    ClientPrice: Number.isFinite(clientPrice) ? clientPrice : 0,
    Total: Number.isFinite(total) ? total : 0,
    Type: isBreakdown ? "BreakDown" : "Cost",
    SubType: isRunCharge ? "Run Charge" : (kind === "fixed" ? "Fixed Charge" : ""),
    DisplayStatus: normalizeDisplayStatus(
      row?.pricingOption ?? row?.DisplayStatus
    ),
    IsLocked:
      row?.isLocked === true ||
      row?.IsLocked === true ||
      String(row?.isLocked ?? row?.IsLocked ?? "").toLowerCase() === "true",
    chargeId: row?.chargeId ?? row?.ChargeId ?? null,
    productionInputLabel:
      row?.productionInputLabel ?? row?.ProductionInputLabel ?? null,
    productionInputValue:
      row?.productionInputValue ?? row?.ProductionInputValue ?? null,
    decoratorId:
      row?.decoratorId ?? row?.DecoratorId ?? fallbackDecoratorId ?? null,
    supplierId: row?.supplierId ?? row?.SupplierId ?? null,
    addressId: row?.addressId ?? row?.AddressId ?? null,
    CurrencyCode: row?.CurrencyCode ?? row?.currencyCode ?? null,
    currencyCode: row?.currencyCode ?? row?.CurrencyCode ?? null,
    PricingCurrencyCode:
      row?.PricingCurrencyCode ?? row?.pricingCurrencyCode ?? null,
    pricingCurrencyCode:
      row?.pricingCurrencyCode ?? row?.PricingCurrencyCode ?? null,
    ConvertedNetCost:
      row?.ConvertedNetCost ?? row?.convertedNetCost ?? null,
    convertedNetCost:
      row?.convertedNetCost ?? row?.ConvertedNetCost ?? null,
    ExchangeRate: row?.ExchangeRate ?? row?.exchangeRate ?? null,
    exchangeRate: row?.exchangeRate ?? row?.ExchangeRate ?? null,
    convertedCurrency: row?.convertedCurrency ?? null,
    productionDepartmentId:
      row?.productionDepartmentId ?? row?.ProductionDepartmentId ?? null,
    sharedGroupKey:
      String(row?.sharedGroupKey ?? row?.SharedGroupKey ?? "").trim() || null,
    sharedQuantityMode:
      row?.sharedQuantityMode ?? row?.SharedQuantityMode ?? null,
    sharedTotalQuantity:
      row?.sharedTotalQuantity ?? row?.SharedTotalQuantity ?? null,
  };
};

export const buildDecorationCopyPayload = (
  sourceDecoration = {},
  { targetDecorationId = null, targetLocation = null } = {}
) => {
  const source = clone(sourceDecoration) || {};
  const details = clone(
    source?.DecoratorDetails ?? source?.decoratorDetails ?? {}
  ) || {};
  const rows = asArray(
    Array.isArray(source?.rows) && source.rows.length
      ? source.rows
      : firstValue(source?.Charge, source?.charge, source?.Charges, source?.charges)
  );
  const fallbackDecoratorId =
    details?.decoratorId ?? details?.DecoratorId ?? null;
  const sharedGroupKey = String(
    details?.sharedGroupKey ??
    details?.SharedGroupKey ??
    source?.sharedGroupKey ??
    source?.SharedGroupKey ??
    ""
  ).trim();
  const sharedQuantityModeRaw = String(
    details?.sharedQuantityMode ??
    details?.SharedQuantityMode ??
    source?.sharedQuantityMode ??
    source?.SharedQuantityMode ??
    ""
  ).trim().toLowerCase();
  const sharedQuantityMode =
    sharedQuantityModeRaw === "pooled" || sharedQuantityModeRaw === "shared"
      ? "pooled"
      : "line";
  const sharedTotalQuantityRaw =
    details?.sharedTotalQuantity ??
    details?.SharedTotalQuantity ??
    source?.sharedTotalQuantity ??
    source?.SharedTotalQuantity ??
    null;
  const sharedTotalQuantity =
    Number.isFinite(Number(sharedTotalQuantityRaw)) &&
    Number(sharedTotalQuantityRaw) > 0
      ? Number(sharedTotalQuantityRaw)
      : null;

  const charges = rows.map((row) => ({
    ...normalizeChargeForCopy(row, fallbackDecoratorId),
    sharedGroupKey:
      String(
        row?.sharedGroupKey ??
        row?.SharedGroupKey ??
        sharedGroupKey ??
        ""
      ).trim() || null,
    sharedQuantityMode:
      row?.sharedQuantityMode ??
      row?.SharedQuantityMode ??
      sharedQuantityMode,
    sharedTotalQuantity:
      row?.sharedTotalQuantity ??
      row?.SharedTotalQuantity ??
      sharedTotalQuantity,
  }));

  const rowBindings = charges.map((charge, index) => ({
    rowIndex: index,
    chargeId: charge?.chargeId ?? null,
    chargeName: charge?.Name ?? "",
    decoratorId: charge?.decoratorId ?? fallbackDecoratorId ?? null,
    supplierId: charge?.supplierId ?? null,
    addressId: charge?.addressId ?? null,
    productionDepartmentId: charge?.productionDepartmentId ?? null,
    productionInputLabel: charge?.productionInputLabel ?? null,
    productionInputValue: charge?.productionInputValue ?? null,
    quantity: charge?.Quantity ?? 0,
    sharedGroupKey: charge?.sharedGroupKey ?? null,
    sharedQuantityMode: charge?.sharedQuantityMode ?? null,
    sharedTotalQuantity: charge?.sharedTotalQuantity ?? null,
  }));

  const distinctChargeIds = [
    ...new Set(
      rowBindings
        .map((binding) => binding.chargeId)
        .filter((value) => value !== null && value !== undefined && value !== "")
    ),
  ];

  const decoratorDetails = {
    ...details,
    rowBindings,
    ...(sharedGroupKey ? { sharedGroupKey, sharedQuantityMode } : {}),
    ...(sharedGroupKey && sharedTotalQuantity != null ? { sharedTotalQuantity } : {}),
  };
  if (distinctChargeIds.length === 1) {
    decoratorDetails.chargeId = distinctChargeIds[0];
  } else if (distinctChargeIds.length > 1) {
    delete decoratorDetails.chargeId;
  }

  const sourceArtworkChoices = asArray(
    firstValue(source?.ArtWorks, source?.artWorks)
  );
  const primaryArtwork =
    source?.artwork ??
    source?.ArtWork ??
    source?.artWork ??
    sourceArtworkChoices[0] ??
    {};
  const artWorks = (
    sourceArtworkChoices.length ? sourceArtworkChoices : [primaryArtwork]
  ).map((artwork) => normalizeArtworkForCopy(artwork));
  const artWork = normalizeArtworkForCopy(primaryArtwork);

  return {
    ...(targetDecorationId != null && targetDecorationId !== ""
      ? { id: targetDecorationId }
      : {}),
    Location:
      String(targetLocation ?? "").trim() ||
      getDecorationLocation(source),
    DecoratorDetails: decoratorDetails,
    Charge: charges,
    ArtWorks: artWorks,
    ArtWork: artWork,
  };
};

// The destination product owns the quantity for copied run charges.
// The breakdown rows (not the source decoration) are the source of truth.
export const getDestinationBreakdownQuantity = (product = {}) => {
  const rows = [product.Pricings, product.pricings, product.Pricing, product.pricing]
    .map(asArray).find((entries) => entries.length > 0) || [];
  const breakdowns = rows.filter((row) =>
    String(row?.priceType ?? row?.PriceType ?? row?.Type ?? row?.type ?? "").trim().toLowerCase() === "breakdown"
  );
  const total = breakdowns.reduce((sum, row) => {
    const quantity = Number(row?.quantity ?? row?.Quantity ?? 0);
    return sum + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0);
  }, 0);
  return total > 0 ? total : null;
};

export const alignCopiedRunChargesToProduct = (decoration = {}, product = {}) => {
  const quantity = getDestinationBreakdownQuantity(product);
  if (quantity == null) return decoration;
  const next = clone(decoration);
  const charges = asArray(next.Charge ?? next.charge ?? next.Charges ?? next.charges);
  const updated = charges.map((row) => {
    const runCharge = chargeKind(row) === "run";
    if (!runCharge) return row;
    const unit = Number(row.ClientPrice ?? row.clientPrice ?? row.Retail ?? row.retail ?? 0);
    const total = Math.round(unit * quantity * 100) / 100;
    return { ...row, Quantity: quantity, quantity, Total: total, total };
  });
  next.Charge = updated;
  if (next.charge != null) next.charge = updated;
  if (next.Charges != null) next.Charges = updated;
  if (next.charges != null) next.charges = updated;
  if (next.DecoratorDetails?.rowBindings) {
    next.DecoratorDetails = {
      ...next.DecoratorDetails,
      rowBindings: next.DecoratorDetails.rowBindings.map((binding) => ({
        ...binding,
        quantity: updated[binding.rowIndex]?.Quantity ?? binding.quantity,
      })),
    };
  }
  return next;
};

export const applyDecorationCopyToProduct = (
  targetProduct = {},
  {
    decoration,
    mode = "add",
    targetDecorationIndex = null,
  } = {}
) => {
  const currentDecorations = getProductDecorations(targetProduct).map((item) =>
    clone(item)
  );
  const nextDecoration = decoration ? alignCopiedRunChargesToProduct(decoration, targetProduct) : null;

  if (!nextDecoration) return targetProduct;

  if (
    mode === "replace" &&
    Number.isInteger(Number(targetDecorationIndex)) &&
    currentDecorations[Number(targetDecorationIndex)]
  ) {
    const index = Number(targetDecorationIndex);
    const existing = currentDecorations[index];
    const existingId =
      existing?.id ?? existing?.DecorationId ?? existing?.decorationId ?? null;
    currentDecorations[index] = {
      ...nextDecoration,
      ...(existingId != null ? { id: existingId } : {}),
    };
  } else {
    const fresh = { ...nextDecoration };
    delete fresh.id;
    delete fresh.DecorationId;
    delete fresh.decorationId;
    currentDecorations.push(fresh);
  }

  return {
    ...targetProduct,
    Decorations: currentDecorations,
    decorations: currentDecorations,
  };
};
